// BottomSheet.js — the one atelier bottom-sheet shell.
//
// Extracted from AccountBottomSheet's mechanics (two-phase slide-up,
// scrim, Esc-to-close, body scroll-lock, reduced-motion honored,
// safe-area padding). Thumb-zone primary surface per DESIGN.md — used
// over centered modals for touch-first provider actions.
//
// Chrome only. Body content passed verbatim via children.

import React, { useEffect, useState } from 'react';

const SHEET_MS = 220;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function BottomSheet({
  open,
  onClose,
  eyebrow,
  title,
  children,
  maxWidth = 520,
  zIndex = 60,
}) {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!open) { setEntered(false); return undefined; }
    if (prefersReducedMotion()) { setEntered(true); return undefined; }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0" style={{ zIndex }} role="dialog" aria-modal="true">
      <div
        onClick={onClose}
        className="absolute inset-0"
        style={{
          backgroundColor: 'rgba(42, 37, 32, 0.42)',
          opacity: entered ? 1 : 0,
          transition: `opacity ${SHEET_MS}ms ease-out`,
        }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute left-0 right-0 bottom-0 mx-auto bg-paper-elev"
        style={{
          maxWidth,
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
          boxShadow: 'var(--shadow-sheet, 0 -8px 40px rgba(42,37,32,0.18))',
          transform: entered ? 'translateY(0)' : 'translateY(100%)',
          transition: `transform ${SHEET_MS}ms ease-out`,
          maxHeight: '85vh',
          overflowY: 'auto',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div className="flex justify-center pt-2.5 pb-1">
          <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: 'var(--line)' }} />
        </div>
        {(eyebrow || title) && (
          <div className="px-5 pt-2 pb-3 border-b border-line">
            {eyebrow && <div className="av-eyebrow mb-1">{eyebrow}</div>}
            {title && (
              <h2 className="font-display text-ink" style={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.2 }}>
                {title}
              </h2>
            )}
          </div>
        )}
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}
